import React, {FC, useState, useEffect, useMemo} from 'react';
import styled, {createGlobalStyle} from 'styled-components';
import {useIntl} from 'react-intl';
import {FoundFlightsSectionMessages} from "./FoundFlightsSectionMessages";
import {Checkbox, Popover, Tooltip} from 'antd';
import {ExclamationCircleOutlined} from '@ant-design/icons';
import {CustomCard} from '../../../../../components/UI/AntCustom/CustomCard';
import {PlaneImage} from '../../../../../components/businessCommon/planeImage/PlaneImage';
import {formatDate, parseStringAsUTCDate, isValidDate} from '../../../../../utils/helpers/dateHelpers/dateHelpers';
import {calculateArrivalDateFromDuration} from '../../../../../utils/helpers/dateHelpers/calculateArrivalDate';
import {formatTimezoneOffsetUTC} from '../../../../../utils/helpers/textDisplayHelpers/DisplayTimezoneOffset';
import {useUserTimezoneOffset} from '../../../../../hooks/useUserTimezone';
import {DisplayAirport} from '../../../../../utils/helpers/textDisplayHelpers/DisplayAirport';
import {CommonFormMessages} from '../../../../../intl/commonMessages/CommonFormMessages';
import {DisplayFlyPrice} from '../../../../../utils/helpers/textDisplayHelpers/DisplayFlyPrice';
import {useAppSelector} from '../../../../../store/store';
import {useCurrencyConverter} from '../../../../../hooks/useCurrencyConverter';
import {getBaseCurrencyId} from '../../../../../utils/helpers/currencyConverter/currencyConverter';
import RouteMiniArrowIcon from '../../../../../media/icons/route_mini_arrow.svg?react';
import InfoIcon from '../../../../../media/icons/info-icon.svg?react';
import PlaneArrivalIcon from '../../../../../media/icons/plane-arrival.svg?react';
import SortingUpIcon from '../../../../../media/icons/sorting-up.svg?react';
import SortingDownIcon from '../../../../../media/icons/sorting-down.svg?react';
import AirplanePopupIcon from '../../../../../media/icons/airplane-popup.svg?react';
import {SubmitBlock} from './SubmitBlock';
import {API} from '../../../../../API/API';
import {AirportApiGetAirportRequest} from '../../../../../proto/generated/api_entities_pb';
import {mapAirportEntityToIAirport} from '../../../../../store/reducers/fleetReducer/fleetMappers';
import {mediaScreenSizes} from '../../../../../utils/constans/styles/screenSizes';

const FoundFlightsSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  width: 100%;
  min-width: 0;
`;

const SubmitBlockContainer = styled.div`
  padding-top: 24px;
  border-top: 1px solid #EEEEEE;
  margin-top: 0;
  
  /* Override SubmitBlock wrapper styles since it's inside a card */
  > div {
    background: transparent;
    border: none;
    border-radius: 0;
    padding: 0;
    gap: 20px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
`;

const SectionTitle = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  color: #000000;
`;

const SortButton = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  cursor: pointer;
  position: absolute;
  right: 0;
  top: 0;
  
  div {
    font-family: Inter, sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 1.2;
    color: #000000;
    white-space: nowrap;
  }
`;

const SortIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const FlightsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
`;

const FlightCard = styled.div`
  background: #FAFAFA;
  border: 1px solid #E4E4E4;
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  min-width: 0;

  &:hover {
    border-color: #1890FF;
  }

  @media (max-width: ${mediaScreenSizes.mobile}) {
    max-width: 100%;
    overflow: hidden;
  }
`;

const FlightCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px 4px 24px;
  min-width: 0;

  @media (max-width: ${mediaScreenSizes.mobile}) {
    padding: 12px 16px 4px 16px;
    flex-wrap: wrap;
    gap: 8px;
  }
`;

const FlightCardHeaderLeft = styled.div`
  display: flex;
  gap: 36px;
  align-items: center;
  min-width: 0;
  flex: 1;

  @media (max-width: ${mediaScreenSizes.mobile}) {
    gap: 12px;
    flex-wrap: wrap;
  }
`;

const FlightCardHeaderLeftBlock = styled.div`
  display: flex;
  gap: 18px;
  align-items: center;
  min-width: 0;

  @media (max-width: ${mediaScreenSizes.mobile}) {
    gap: 8px;
  }
`;

const FlightCardNameBlock = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  cursor: pointer;
  min-width: 0;

  &:hover {
    opacity: 0.7;
  }
`;

const FlightCardName = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
  color: #000000;
  letter-spacing: -0.14px;
  white-space: nowrap;

  @media (max-width: ${mediaScreenSizes.mobile}) {
    white-space: normal;
    overflow-wrap: break-word;
    word-break: break-word;
  }
`;

const FlightCardDottedLine = styled.div`
  width: 140.383px;
  height: 1px;
  background-image: repeating-linear-gradient(
    to right,
    #000 0px,
    #000 14.048px,
    transparent 14.048px,
    transparent 18.048px
  );
  margin-top: 1px;

  @media (max-width: ${mediaScreenSizes.mobile}) {
    max-width: 100%;
  }
`;

const FlightCardCompany = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const FlightCardCompanyName = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  min-width: 0;
`;

const FlightCardCompanyText = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  color: #000000;
  max-width: 300px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: ${mediaScreenSizes.mobile}) {
    max-width: 100%;
  }
`;

const FlightCardHeaderRight = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FlightCardDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-start;
  justify-content: space-between;
  padding: 16px 24px;
  position: relative;
  min-width: 0;

  @media (max-width: ${mediaScreenSizes.mobile}) {
    padding: 16px;
  }
`;

const FlightCardImage = styled.div`
  width: 100px;
  height: 70px;
  border-radius: 5px;
  overflow: hidden;
  background: #FFFFFF;
  flex-shrink: 0;
`;

const FlightCardDetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const FlightCardDetailLabel = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
  color: #000000;
  white-space: nowrap;

  @media (max-width: ${mediaScreenSizes.mobile}) {
    white-space: normal;
  }
`;

const FlightCardDetailValue = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #000000;
  letter-spacing: -0.14px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
  overflow-wrap: break-word;
  word-break: break-word;
  min-width: 0;
`;

const FlightCardDetailTimezone = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 18px;
  color: #000000;
  opacity: 0.5;
  letter-spacing: -0.14px;
  white-space: nowrap;
`;

const DepartureTimeWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
`;

const WarningIconWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  color: #FF4D4F;
  cursor: help;
  margin-top: 2px; /* Slight offset to align with text baseline */
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const PriceLabelBlock = styled.div`
  display: flex;
  gap: 2px;
  align-items: center;
`;

const PriceValue = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #000000;
  letter-spacing: -0.14px;
  white-space: nowrap;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  @media (max-width: ${mediaScreenSizes.mobile}) {
    white-space: normal;
    overflow-wrap: break-word;
  }
`;

const PriceSecondary = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 18px;
  color: #666666;
  letter-spacing: -0.12px;
`;

const InfoIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background: #1890FF;
  border-radius: 100px;
  flex-shrink: 0;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const RouteAirports = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  min-width: 0;
  flex-wrap: wrap;

  div {
    font-weight: 400;
    font-size: 14px;
    line-height: 1.2;
    color: #000000;
    letter-spacing: -0.14px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    @media (max-width: ${mediaScreenSizes.mobile}) {
      white-space: normal;
      overflow-wrap: break-word;
      word-break: break-word;
    }
  }
`;

const RouteAirportsMore = styled.div`
  font-weight: 600;
  font-size: 14px;
  line-height: 17px;
  letter-spacing: -0.01em;
`;

const AirplanePopupIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  cursor: pointer;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const CustomPopoverStyles = createGlobalStyle`
  .custom-warning-popover {
    .ant-popover-inner {
      background: #FFFFFF;
      border-radius: 8px;
      box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.3), 0px 2px 6px 2px rgba(0, 0, 0, 0.15);
      max-width: 310px;
    }
    
    .ant-popover-inner-content {
      padding: 16px;
    }
    
    .ant-popover-arrow {
      display: block;
    }
    
    .ant-popover-arrow-content {
      background: #FFFFFF;
      box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.3), 0px 2px 6px 2px rgba(0, 0, 0, 0.15);
    }
    
    &.ant-popover-placement-topRight .ant-popover-arrow {
      right: 8px !important;
      left: auto !important;
    }
  }
`;

const PopoverContent = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  color: #000000;
  white-space: pre-wrap;
  max-width: 310px;
`;

const AirplaneMarkerWrapper = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;


export interface IFlight {
  id: string;
  planeName: string;
  registrationNumber?: string;
  companyName: string;
  price: number;
  preliminaryPrice?: number;
  currency: { id: number; name: string }; // Base currency (where preliminaryPrice is calculated)
  planeCurrency?: { id: number; name: string }; // Plane's original currency
  priceInUserCurrency?: number; // Конвертированная цена в валюте пользователя
  userCurrency?: { id: number; name: string }; // Валюта пользователя
  priceInPlaneCurrency?: number; // Конвертированная цена в валюте самолета
  routeFrom: {
    code: string;
    location_and_name: string;
  };
  routeTo: {
    code: string;
    location_and_name: string;
  };
  itinerary?: Array<{ // Full itinerary for multileg routes
    code: string;
    location_and_name: string;
  }>;
  availabilityStartDate: Date;
  availabilityEndDate: Date;
  timezoneOffset: number;
  pax: number;
  arrivalTime?: string; // Deprecated: use arrivalDate instead
  arrivalDate?: Date; // Calculated arrival date/time
  adjustedDepartureDateTime?: string; // Adjusted departure date/time in Y-m-d H:i:s format (if requested time conflicts with event)
  flightDurationMinutes?: number; // Total flight duration in minutes
  lastLegFlightDurationMinutes?: number; // Duration of the last leg only (for multileg routes)
  arrivalAirport: string;
  baseAirport: string;
  aircraftCategory: string;
  planeImageFiles: any[];
  isSelected?: boolean;
  hasRouteWarning?: boolean;
  hasArrivalWarning?: boolean;
  routeErrorMessage?: string; // Сообщение об ошибке маршрута
  hasPriceError?: boolean; // Флаг, что цена не рассчитана из-за ошибки
  positioningDurationMinutes?: number; // Длительность позиционирования в минутах
  positioningTechnicalStopCount?: number; // Количество технических остановок при позиционировании
  positioningFromAirportId?: number; // Airport where aircraft is located before positioning
}

interface IFoundFlightsSection {
  flights?: IFlight[];
  selectedFlights?: Set<string>;
  setSelectedFlights?: (selectedFlights: Set<string>) => void;
  onFlightNameClick?: (flight: IFlight) => void;
  onCreateRequest?: () => void;
}

export const FoundFlightsSection: FC<IFoundFlightsSection> = ({ 
  flights = [],
  selectedFlights: externalSelectedFlights,
  setSelectedFlights: externalSetSelectedFlights,
  onFlightNameClick,
  onCreateRequest
}) => {
  const intl = useIntl();
  const [internalSelectedFlights, setInternalSelectedFlights] = useState<Set<string>>(new Set());
  const [isSortAscending, setIsSortAscending] = useState<boolean>(true);
  const userCurrency = useAppSelector(state => state.user.oneItemObject.item?.currency);
  const companyCurrency = useAppSelector(state => state.company.oneItemObject.item?.currency);
  const targetCurrency = userCurrency || companyCurrency;
  const {convertSync, isReady} = useCurrencyConverter();
  const lastSearchParams = useAppSelector(state => state.newRequest.lastSearchParams);
  const offset = useUserTimezoneOffset();
  const timezoneDisplay = useMemo(() => formatTimezoneOffsetUTC(offset), [offset]);
  
  // State to store positioning airport codes
  const [positioningAirports, setPositioningAirports] = useState<Record<number, string>>({});
  
  // Load positioning airport information
  useEffect(() => {
    const loadPositioningAirports = async () => {
      const airportIds = flights
        .filter(flight => flight.positioningFromAirportId && flight.positioningDurationMinutes && flight.positioningDurationMinutes > 0)
        .map(flight => flight.positioningFromAirportId!)
        .filter((id, index, self) => self.indexOf(id) === index); // Get unique IDs
      
      if (airportIds.length === 0) return;
      
      const airports: Record<number, string> = {};
      await Promise.all(
        airportIds.map(async (airportId) => {
          try {
            const req = new AirportApiGetAirportRequest().setAirportid(airportId);
            const airportEntity = (await API.getAirportById(req)).getAirport();
            if (airportEntity) {
              const airport = mapAirportEntityToIAirport(airportEntity);
              airports[airportId] = airport.icaoCode || airport.iataCode || '';
            }
          } catch (error) {
            console.error(`Failed to load positioning airport ${airportId}:`, error);
          }
        })
      );
      setPositioningAirports(airports);
    };
    
    loadPositioningAirports();
  }, [flights]);
  
  // Получаем ID базовой валюты
  const baseCurrencyId = useMemo(() => {
    if (isReady) {
      return getBaseCurrencyId();
    }
    return null;
  }, [isReady]);
  
  // Конвертируем цены в валюту пользователя
  const flightsWithConvertedPrices = useMemo(() => {
    // Если priceInUserCurrency уже установлен в NewTripRequestPage, используем его
    // Иначе конвертируем здесь
    return flights.map(flight => {
      // Если уже есть конвертированная цена из NewTripRequestPage, используем её
      if (flight.priceInUserCurrency && flight.userCurrency) {
        // Calculate price in plane's currency if it differs from broker's currency
        let priceInPlaneCurrency: number | undefined = undefined;
        if (
          flight.planeCurrency &&
          flight.userCurrency &&
          flight.planeCurrency.id !== flight.userCurrency.id &&
          isReady &&
          baseCurrencyId &&
          flight.preliminaryPrice &&
          flight.preliminaryPrice > 0
        ) {
          try {
            // Convert from base currency to plane's currency
            priceInPlaneCurrency = convertSync(
              flight.preliminaryPrice,
              baseCurrencyId,
              flight.planeCurrency.id
            );
            priceInPlaneCurrency = Math.round(priceInPlaneCurrency * 100) / 100;
          } catch (error) {
            console.error('[FoundFlightsSection] Failed to convert to plane currency:', error);
          }
        }
        return {
          ...flight,
          priceInPlaneCurrency,
        };
      }

      // Если нет targetCurrency или конвертер не готов, возвращаем как есть
      if (!targetCurrency || !isReady || !baseCurrencyId) {
        return flight;
      }

      // Если валюта самолета совпадает с валютой пользователя, конвертация не нужна
      if (flight.currency.id === targetCurrency.id) {
        return {
          ...flight,
          priceInUserCurrency: flight.preliminaryPrice ?? flight.price,
          userCurrency: targetCurrency,
        };
      }

      // Конвертируем цену в валюту пользователя
      // preliminaryPrice приходит в базовой валюте, поэтому используем базовую валюту для конвертации
      const originalPrice = flight.preliminaryPrice ?? flight.price;
      const fromCurrencyId = baseCurrencyId;
      
      let convertedPrice = convertSync(
        originalPrice,
        fromCurrencyId,
        targetCurrency.id
      );
      
      // Округляем конвертированную цену до 2 знаков после запятой
      convertedPrice = Math.round(convertedPrice * 100) / 100;

      // Calculate price in plane's currency if it differs from broker's currency
      let priceInPlaneCurrency: number | undefined = undefined;
      if (
        flight.planeCurrency &&
        flight.planeCurrency.id !== targetCurrency.id &&
        flight.preliminaryPrice &&
        flight.preliminaryPrice > 0
      ) {
        try {
          priceInPlaneCurrency = convertSync(
            flight.preliminaryPrice,
            baseCurrencyId,
            flight.planeCurrency.id
          );
          priceInPlaneCurrency = Math.round(priceInPlaneCurrency * 100) / 100;
        } catch (error) {
          console.error('[FoundFlightsSection] Failed to convert to plane currency:', error);
        }
      }

      return {
        ...flight,
        priceInUserCurrency: convertedPrice,
        userCurrency: targetCurrency,
        priceInPlaneCurrency,
      };
    });
  }, [flights, targetCurrency, convertSync, isReady, baseCurrencyId]);

  // Используем внешнее состояние, если оно передано, иначе внутреннее
  const selectedFlights = externalSelectedFlights !== undefined ? externalSelectedFlights : internalSelectedFlights;
  const setSelectedFlights = externalSetSelectedFlights || setInternalSelectedFlights;

  const handleFlightSelect = (flightId: string, checked: boolean) => {
    const newSelected = new Set(selectedFlights);
    if (checked) {
      newSelected.add(flightId);
    } else {
      newSelected.delete(flightId);
    }
    setSelectedFlights(newSelected);
  };

  const handleSortClick = () => {
    setIsSortAscending(!isSortAscending);
  };

  // Сортируем flights по цене в валюте пользователя (если доступна), иначе по оригинальной цене
  const sortedFlights = [...flightsWithConvertedPrices].sort((a, b) => {
    const priceA = (a.priceInUserCurrency ?? a.preliminaryPrice ?? a.price) ?? 0;
    const priceB = (b.priceInUserCurrency ?? b.preliminaryPrice ?? b.price) ?? 0;
    
    if (isSortAscending) {
      return priceA - priceB; // От дешевых к дорогим
    } else {
      return priceB - priceA; // От дорогих к дешевым
    }
  });

  const handleCardClick = (flight: IFlight, e: React.MouseEvent) => {
    // Не обрабатываем клик, если кликнули на название самолета (пунктир) или чекбокс
    const target = e.target as HTMLElement;
    if (target.closest('.flight-card-name-block') || target.closest('input[type="checkbox"]')) {
      return;
    }
    // Переключаем выбор самолета
    const isSelected = selectedFlights.has(flight.id);
    handleFlightSelect(flight.id, !isSelected);
  };

  const handleNameBlockClick = (flight: IFlight, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onFlightNameClick) {
      onFlightNameClick(flight);
    }
  };

  return (
    <>
      <CustomPopoverStyles />
      <CustomCard width={'max'} isContainerCard={true} borderless={false}>
        <FoundFlightsSectionWrapper>
        <SectionHeader>
        <SectionTitle>
          {intl.formatMessage(FoundFlightsSectionMessages.foundFlights)} ({flights.length})
        </SectionTitle>
        <SortButton onClick={handleSortClick}>
          <div>{intl.formatMessage(FoundFlightsSectionMessages.sortByPrice)}</div>
          <SortIconWrapper>
            {isSortAscending ? <SortingUpIcon /> : <SortingDownIcon />}
          </SortIconWrapper>
        </SortButton>
      </SectionHeader>

      <FlightsList>
        {sortedFlights.map((flight) => (
          <FlightCard key={flight.id} onClick={(e) => handleCardClick(flight, e)}>
            <FlightCardHeader>
              <FlightCardHeaderLeft>
                <FlightCardHeaderLeftBlock>
                  <FlightCardNameBlock 
                    className="flight-card-name-block"
                    onClick={(e) => handleNameBlockClick(flight, e)}
                  >
                    <FlightCardName>
                      {flight.planeName}{flight.aircraftCategory ? `, ${flight.aircraftCategory}` : ''}, {intl.formatMessage(FoundFlightsSectionMessages.pax)}: {flight.pax}
                    </FlightCardName>
                    <FlightCardDottedLine />
                  </FlightCardNameBlock>
                </FlightCardHeaderLeftBlock>
                <FlightCardCompany>
                  <FlightCardCompanyName>
                    <FlightCardCompanyText>{flight.companyName}</FlightCardCompanyText>
                  </FlightCardCompanyName>
                </FlightCardCompany>
              </FlightCardHeaderLeft>
              <FlightCardHeaderRight>
                <Checkbox
                  checked={selectedFlights.has(flight.id)}
                  onChange={(e) => handleFlightSelect(flight.id, e.target.checked)}
                />
              </FlightCardHeaderRight>
            </FlightCardHeader>

            <FlightCardDetails>
              <FlightCardImage>
                <PlaneImage files={flight.planeImageFiles} width="100px" height="70px" />
              </FlightCardImage>

              <FlightCardDetailItem>
                <PriceLabelBlock>
                  <FlightCardDetailLabel>
                    {intl.formatMessage(FoundFlightsSectionMessages.price)}
                  </FlightCardDetailLabel>
                  {flight.routeErrorMessage ? (
                    <Popover
                      content={
                        <PopoverContent>
                          {flight.routeErrorMessage}
                        </PopoverContent>
                      }
                      placement="topRight"
                      align={{ offset: [10, -10] }}
                      overlayClassName="custom-warning-popover"
                      overlayStyle={{
                        padding: 0,
                      }}
                      overlayInnerStyle={{
                        padding: 0,
                        borderRadius: '8px',
                      }}
                      getPopupContainer={(trigger) => trigger.parentElement || document.body}
                    >
                      <InfoIconWrapper>
                        <InfoIcon />
                      </InfoIconWrapper>
                    </Popover>
                  ) : (
                    <InfoIconWrapper>
                      <InfoIcon />
                    </InfoIconWrapper>
                  )}
                </PriceLabelBlock>
                <PriceValue>
                  {flight.hasPriceError ? (
                    <span>
                      {intl.formatMessage(FoundFlightsSectionMessages.priceNotCalculated)}
                    </span>
                  ) : (
                    <>
                      {flight.priceInUserCurrency && flight.userCurrency ? (
                        <>
                          <div>{DisplayFlyPrice(flight.priceInUserCurrency, flight.userCurrency)}</div>
                          {flight.priceInPlaneCurrency && flight.planeCurrency && flight.planeCurrency.id !== flight.userCurrency.id && (
                            <PriceSecondary>
                              {DisplayFlyPrice(flight.priceInPlaneCurrency, flight.planeCurrency)}
                            </PriceSecondary>
                          )}
                          {!flight.priceInPlaneCurrency && flight.currency.id !== flight.userCurrency.id && (
                            <PriceSecondary>
                              {DisplayFlyPrice(flight.preliminaryPrice ?? flight.price, flight.currency)}
                            </PriceSecondary>
                          )}
                        </>
                      ) : (
                        <>
                          {DisplayFlyPrice(flight.preliminaryPrice ?? flight.price, flight.currency)}
                          {flight.priceInPlaneCurrency && flight.planeCurrency && flight.planeCurrency.id !== flight.currency.id && (
                            <PriceSecondary>
                              {DisplayFlyPrice(flight.priceInPlaneCurrency, flight.planeCurrency)}
                            </PriceSecondary>
                          )}
                        </>
                      )}
                    </>
                  )}
                </PriceValue>
              </FlightCardDetailItem>

              <FlightCardDetailItem>
                <FlightCardDetailLabel>
                  {intl.formatMessage(FoundFlightsSectionMessages.route)}
                </FlightCardDetailLabel>
                <Tooltip
                  title={
                    flight.itinerary && flight.itinerary.length > 0
                      ? flight.itinerary.map(airport => airport.location_and_name || airport.code).join(' → ')
                      : `${flight.routeFrom.location_and_name || flight.routeFrom.code} → ${flight.routeTo.location_and_name || flight.routeTo.code}`
                  }
                  placement="top"
                >
                  <RouteAirports>
                    {(() => {
                      // Use itinerary if available, otherwise use routeFrom and routeTo
                      const airports = flight.itinerary && flight.itinerary.length > 0
                        ? flight.itinerary
                        : [flight.routeFrom, flight.routeTo];
                      
                      // Build airport codes display array (reusing logic from OrderCharterCard)
                      const routeAirportsDisplay = airports.map(airport => airport.code);
                      
                      // For 3 or fewer airports, show all. For more than 3, show first 2 and (+N)
                      const firstSliceCount = routeAirportsDisplay.length <= 3 ? routeAirportsDisplay.length : 2;
                      const airportsToDisplay = routeAirportsDisplay.slice(0, firstSliceCount);
                      const moreAirportsCount = routeAirportsDisplay.length - firstSliceCount;
                      
                      return (
                        <>
                          {airportsToDisplay.map((airportCode, index) => {
                            if (index === 0) {
                              return <div key={index}>{airportCode}</div>;
                            }
                            return (
                              <React.Fragment key={index}>
                                <RouteMiniArrowIcon/>
                                <div>{airportCode}</div>
                              </React.Fragment>
                            );
                          })}
                          {moreAirportsCount > 0 && (
                            <>
                              <RouteMiniArrowIcon/>
                              <RouteAirportsMore>
                                (+{moreAirportsCount})
                              </RouteAirportsMore>
                            </>
                          )}
                          {flight.hasRouteWarning && (
                            <Popover
                              content={
                                <PopoverContent>
                                  {flight.routeErrorMessage || intl.formatMessage(FoundFlightsSectionMessages.routeTooltip)}
                                </PopoverContent>
                              }
                              placement="topRight"
                              align={{ offset: [10, -10] }}
                              overlayClassName="custom-warning-popover"
                              overlayStyle={{
                                padding: 0,
                              }}
                              overlayInnerStyle={{
                                padding: 0,
                                borderRadius: '8px',
                              }}
                              getPopupContainer={(trigger) => trigger.parentElement || document.body}
                            >
                              <AirplanePopupIconWrapper>
                                <AirplanePopupIcon />
                              </AirplanePopupIconWrapper>
                            </Popover>
                          )}
                        </>
                      );
                    })()}
                  </RouteAirports>
                </Tooltip>
              </FlightCardDetailItem>

              <FlightCardDetailItem>
                <FlightCardDetailLabel>
                  {intl.formatMessage(FoundFlightsSectionMessages.departTime)}
                </FlightCardDetailLabel>
                <FlightCardDetailValue>
                  {(() => {
                    // Helper to normalize time format to HH:mm:ss
                    const normalizeTime = (time: string | undefined): string => {
                      if (!time) return '00:00:00';
                      return time.split(':').length === 2 ? `${time}:00` : time;
                    };

                    // Helper to combine date and time strings into UTC Date
                    const combineDateTime = (dateStr: string, timeStr: string | undefined): Date | null => {
                      try {
                        const normalizedTime = normalizeTime(timeStr);
                        const dateTimeString = `${dateStr} ${normalizedTime}`;
                        const parsed = parseStringAsUTCDate(dateTimeString);
                        return isValidDate(parsed) ? parsed : null;
                      } catch {
                        return null;
                      }
                    };

                    // Get departure date and time - prioritize adjusted departure from backend
                    let departureDate: Date | null = null;
                    let requestedDepartureDate: Date | null = null;
                    let isAdjusted = false;
                    
                    // First priority: use adjusted departure datetime from backend if available
                    if (flight.adjustedDepartureDateTime) {
                      const parsed = parseStringAsUTCDate(flight.adjustedDepartureDateTime);
                      if (isValidDate(parsed)) {
                        departureDate = parsed;
                        isAdjusted = true;
                      }
                    }
                    
                    // Get requested departure time for comparison
                    const multilegRoutes = lastSearchParams?.multilegRoutes;
                    if (multilegRoutes && multilegRoutes.length > 0) {
                      const firstRoute = multilegRoutes[0];
                      if (firstRoute.departureDate) {
                        requestedDepartureDate = combineDateTime(firstRoute.departureDate, firstRoute.departureTime);
                        if (!departureDate) {
                          departureDate = requestedDepartureDate;
                        }
                      }
                    } else if (lastSearchParams?.departureDate) {
                      const dateStr = lastSearchParams.departureDate.toISOString().split('T')[0];
                      requestedDepartureDate = combineDateTime(dateStr, lastSearchParams.departureTime) || lastSearchParams.departureDate;
                      if (!departureDate) {
                        departureDate = requestedDepartureDate;
                      }
                    }
                    
                    // Calculate time difference if adjusted
                    let timeDifferenceText: string | null = null;
                    if (isAdjusted && departureDate && requestedDepartureDate) {
                      const diffMs = departureDate.getTime() - requestedDepartureDate.getTime();
                      const diffMinutes = Math.round(diffMs / (1000 * 60));
                      
                      if (diffMinutes > 0) {
                        if (diffMinutes < 60) {
                          timeDifferenceText = `${diffMinutes} ${diffMinutes === 1 ? 'minute' : 'minutes'}`;
                        } else {
                          const hours = Math.floor(diffMinutes / 60);
                          const minutes = diffMinutes % 60;
                          if (minutes === 0) {
                            timeDifferenceText = `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
                          } else {
                            timeDifferenceText = `${hours}:${minutes.toString().padStart(2, '0')}`;
                          }
                        }
                      }
                    }
                    
                    if (departureDate) {
                      return (
                        <DepartureTimeWrapper>
                          <div>
                            {formatDate(departureDate, offset, "datetime")}
                            <FlightCardDetailTimezone>
                              {timezoneDisplay}
                            </FlightCardDetailTimezone>
                          </div>
                          {isAdjusted && timeDifferenceText && (
                            <Tooltip
                              title={intl.formatMessage(FoundFlightsSectionMessages.adjustedDepartureWarning, {
                                timeDifference: timeDifferenceText,
                              })}
                            >
                              <WarningIconWrapper>
                                <ExclamationCircleOutlined />
                              </WarningIconWrapper>
                            </Tooltip>
                          )}
                        </DepartureTimeWrapper>
                      );
                    }
                    
                    return <span style={{opacity: 0.5}}>{intl.formatMessage(CommonFormMessages.noData)}</span>;
                  })()}
                </FlightCardDetailValue>
              </FlightCardDetailItem>

              <FlightCardDetailItem>
                <FlightCardDetailLabel>
                  {intl.formatMessage(FoundFlightsSectionMessages.arrivalTime)}
                </FlightCardDetailLabel>
                <FlightCardDetailValue>
                  {(() => {
                    // Helper to normalize time format to HH:mm:ss
                    const normalizeTime = (time: string | undefined): string => {
                      if (!time) return '00:00:00';
                      return time.split(':').length === 2 ? `${time}:00` : time;
                    };

                    // Helper to combine date and time strings into UTC Date
                    const combineDateTime = (dateStr: string, timeStr: string | undefined): Date | null => {
                      try {
                        const normalizedTime = normalizeTime(timeStr);
                        const dateTimeString = `${dateStr} ${normalizedTime}`;
                        const parsed = parseStringAsUTCDate(dateTimeString);
                        return isValidDate(parsed) ? parsed : null;
                      } catch {
                        return null;
                      }
                    };

                    // Calculate arrival time based on adjusted departure (if available) + flight duration
                    let calculatedArrivalDate: Date | null = null;
                    let departureDateForArrival: Date | null = null;

                    // Priority 1: Use adjusted departure time if available
                    if (flight.adjustedDepartureDateTime) {
                      const parsed = parseStringAsUTCDate(flight.adjustedDepartureDateTime);
                      if (isValidDate(parsed)) {
                        departureDateForArrival = parsed;
                      }
                    }

                    // Priority 2: For multileg routes, get last leg departure time (adjusted or requested)
                    if (!departureDateForArrival) {
                      const multilegRoutes = lastSearchParams?.multilegRoutes;
                      if (multilegRoutes && multilegRoutes.length > 0) {
                        const lastRoute = multilegRoutes[multilegRoutes.length - 1];
                        if (lastRoute.departureDate) {
                          departureDateForArrival = combineDateTime(lastRoute.departureDate, lastRoute.departureTime);
                        }
                      }
                    }

                    // Priority 3: Simple route departure time
                    if (!departureDateForArrival && lastSearchParams?.departureDate) {
                      const dateStr = lastSearchParams.departureDate.toISOString().split('T')[0];
                      departureDateForArrival = combineDateTime(dateStr, lastSearchParams.departureTime) || lastSearchParams.departureDate;
                    }

                    // Priority: Use backend-calculated arrivalDate if available (it's already calculated correctly from adjusted routes)
                    // The backend arrivalDate is calculated from the adjusted last leg departure + last leg duration for multileg flights
                    // Only calculate from departure + duration if backend arrivalDate is not available
                    let arrivalDate: Date | null = flight.arrivalDate || null;
                    
                    // Fallback: Calculate arrival from departure + flight duration if backend arrivalDate is not available
                    if (!arrivalDate && departureDateForArrival) {
                      // For multileg, use last leg flight duration; otherwise use total flight duration
                      const flightDuration = lastSearchParams?.multilegRoutes && lastSearchParams.multilegRoutes.length > 0
                        ? flight.lastLegFlightDurationMinutes
                        : flight.flightDurationMinutes;

                      if (flightDuration && flightDuration > 0) {
                        arrivalDate = calculateArrivalDateFromDuration(
                          departureDateForArrival,
                          flightDuration,
                          0
                        );
                      }
                    }

                    if (arrivalDate) {
                      // Get departure date for comparison (for formatting)
                      const departureDateForComparison = departureDateForArrival || 
                        (lastSearchParams?.multilegRoutes && lastSearchParams.multilegRoutes.length > 0
                          ? (() => {
                              const lastRoute = lastSearchParams.multilegRoutes[lastSearchParams.multilegRoutes.length - 1];
                              if (lastRoute.departureDate) {
                                return combineDateTime(lastRoute.departureDate, lastRoute.departureTime);
                              }
                              return null;
                            })()
                          : lastSearchParams?.departureDate) || null;

                      // Format arrival time: show date if it differs from departure date, otherwise just time
                      let arrivalTimeStr: string;
                      if (departureDateForComparison) {
                        const departureDateStr = departureDateForComparison.toISOString().split('T')[0];
                        const arrivalDateStr = arrivalDate.toISOString().split('T')[0];
                        
                        if (departureDateStr !== arrivalDateStr) {
                          arrivalTimeStr = formatDate(arrivalDate, offset, "datetime");
                        } else {
                          arrivalTimeStr = formatDate(arrivalDate, offset, "time");
                        }
                      } else {
                        arrivalTimeStr = formatDate(arrivalDate, offset, "datetime");
                      }
                      
                      return (
                        <>
                          {arrivalTimeStr} {flight.arrivalAirport}
                          <FlightCardDetailTimezone>
                            {timezoneDisplay}
                          </FlightCardDetailTimezone>
                        </>
                      );
                    }
                    
                    // Fallback to old arrivalTime string format
                    return (
                      <>
                        {flight.arrivalTime || ''} {flight.arrivalAirport}
                        <FlightCardDetailTimezone>
                          {timezoneDisplay}
                        </FlightCardDetailTimezone>
                      </>
                    );
                  })()}
                </FlightCardDetailValue>
                {flight.hasArrivalWarning && (
                  <Popover
                    content={
                      <PopoverContent>
                        {intl.formatMessage(FoundFlightsSectionMessages.arrivalTimeTooltip)}
                      </PopoverContent>
                    }
                    placement="topRight"
                    align={{ offset: [10, -10] }}
                    overlayClassName="custom-warning-popover"
                    overlayStyle={{
                      padding: 0,
                    }}
                    overlayInnerStyle={{
                      padding: 0,
                      borderRadius: '8px',
                    }}
                    getPopupContainer={(trigger) => trigger.parentElement || document.body}
                  >
                    <AirplanePopupIconWrapper>
                      <AirplanePopupIcon />
                    </AirplanePopupIconWrapper>
                  </Popover>
                )}
              </FlightCardDetailItem>

              {flight.positioningDurationMinutes !== undefined && flight.positioningDurationMinutes > 0 && (
                <FlightCardDetailItem>
                  <FlightCardDetailLabel>
                    {intl.formatMessage(FoundFlightsSectionMessages.positioning)}
                  </FlightCardDetailLabel>
                  <FlightCardDetailValue>
                    {`${Math.floor(flight.positioningDurationMinutes / 60)}:${String(flight.positioningDurationMinutes % 60).padStart(2, '0')}`}
                    {flight.positioningFromAirportId && positioningAirports[flight.positioningFromAirportId] && (
                      <> {positioningAirports[flight.positioningFromAirportId]}</>
                    )}
                  </FlightCardDetailValue>
                </FlightCardDetailItem>
              )}

              {flight.positioningTechnicalStopCount !== undefined && flight.positioningTechnicalStopCount > 0 && (
                <FlightCardDetailItem>
                  <FlightCardDetailLabel>
                    {intl.formatMessage(FoundFlightsSectionMessages.positioningTechnicalStops)}
                  </FlightCardDetailLabel>
                  <FlightCardDetailValue>{flight.positioningTechnicalStopCount}</FlightCardDetailValue>
                </FlightCardDetailItem>
              )}

              <FlightCardDetailItem>
                <FlightCardDetailLabel>
                  {intl.formatMessage(FoundFlightsSectionMessages.baseAirport)}
                </FlightCardDetailLabel>
                <FlightCardDetailValue>{flight.baseAirport}</FlightCardDetailValue>
              </FlightCardDetailItem>
            </FlightCardDetails>
          </FlightCard>
        ))}
      </FlightsList>
      {onCreateRequest && (
        <SubmitBlockContainer>
          <SubmitBlock 
            selectedFlightsCount={selectedFlights.size}
            onCreateRequest={onCreateRequest}
          />
        </SubmitBlockContainer>
      )}
        </FoundFlightsSectionWrapper>
      </CustomCard>
    </>
  );
};

